import React from 'react';
import { createBrowserRouter } from "react-router-dom";
import featureFlags from './config/featureFlags';
import { track } from './api/services/analyticsService';

import Layout from "./components/Layout";
import AuthLayout from "./components/AuthLayout";
import ErrorBoundary from "./components/ErrorBoundary";
import { AdminRoute } from "./components/Admin";

// Core pages — lazy-loaded to reduce initial bundle size.
const Home           = React.lazy(() => import('./pages/Home'));
const About          = React.lazy(() => import('./pages/About'));
const Contact        = React.lazy(() => import('./pages/Contact'));
const Books          = React.lazy(() => import('./pages/Books'));
const Philosophy     = React.lazy(() => import('./pages/Philosophy'));
const BlogList       = React.lazy(() => import('./components/Blog/BlogList'));
const BlogDetail     = React.lazy(() => import('./components/Blog/BlogDetail'));
const LoginForm      = React.lazy(() => import('./components/Auth/LoginForm'));
const RegisterForm   = React.lazy(() => import('./components/Auth/RegisterForm'));
const Resources      = React.lazy(() => import('./pages/Resources'));
const Curriculum     = React.lazy(() => import('./pages/resources/Curriculum'));
const Pedagogy       = React.lazy(() => import('./pages/resources/Pedagogy'));
const Assessment     = React.lazy(() => import('./pages/resources/Assessment'));
const BookResources       = React.lazy(() => import('./pages/BookResources'));
const BookResourceDetail  = React.lazy(() => import('./pages/BookResourceDetail'));
const NotFoundPage   = React.lazy(() => import('./pages/NotFoundPage'));

// Admin pages — lazy-loaded since they're behind auth and used by <1% of users.
const AdminLayout         = React.lazy(() => import('./components/Admin/AdminLayout'));
const Dashboard           = React.lazy(() => import('./components/Admin/Dashboard'));
const UsersPage           = React.lazy(() => import('./components/Admin/UsersPage'));
const BlogsPage           = React.lazy(() => import('./components/Admin/BlogsPage'));
const BooksPage           = React.lazy(() => import('./components/Admin/BooksPage'));
const BookResourcesPage   = React.lazy(() => import('./components/Admin/BookResourcesPage'));
const CategoriesPage      = React.lazy(() => import('./components/Admin/CategoriesPage'));
const OrdersPage          = React.lazy(() => import('./components/Admin/OrdersPage'));
const BundlesPage         = React.lazy(() => import('./components/Admin/BundlesPage'));
const StoreCategoriesPage = React.lazy(() => import('./components/Admin/StoreCategoriesPage'));
const QuotesPage          = React.lazy(() => import('./components/Admin/QuotesPage'));
const CouponsPage         = React.lazy(() => import('./components/Admin/CouponsPage'));

// Ecommerce pages — lazy-loaded so they never inflate the main bundle.
// NOTE: There is NO standalone shop page. Product browsing lives on /books.
const ProductDetail   = React.lazy(() => import('./features/catalog/pages/ProductDetail'));
const BundleDetail    = React.lazy(() => import('./features/catalog/pages/BundleDetail'));
const CartPage        = React.lazy(() => import('./features/cart/pages/CartPage'));
const CheckoutPage    = React.lazy(() => import('./features/checkout/pages/CheckoutPage'));
const OrderConfirm    = React.lazy(() => import('./features/checkout/pages/OrderConfirm'));
const OrderHistory    = React.lazy(() => import('./features/account/pages/OrderHistory'));
const OrderDetail     = React.lazy(() => import('./features/account/pages/OrderDetail'));
const Downloads       = React.lazy(() => import('./features/account/pages/Downloads'));
const Returns         = React.lazy(() => import('./features/account/pages/Returns'));
const QuotePage       = React.lazy(() => import('./features/quote/pages/QuotePage'));
const EducatorVerification = React.lazy(() => import('./features/account/pages/EducatorVerification'));
const QuoteHistory    = React.lazy(() => import('./features/account/pages/QuoteHistory'));
const ProfilePage     = React.lazy(() => import('./features/account/pages/ProfilePage'));

const ShopFallback = (
  <div className="p-8 text-center text-sm text-gray-400">Loading…</div>
);

const router = createBrowserRouter([
  {
    path: "/",
    // ErrorBoundary + Suspense wraps Layout so lazy-loaded children
    // share one fallback without affecting synchronous pages.
    element: (
      <ErrorBoundary>
        <React.Suspense fallback={ShopFallback}>
          <Layout />
        </React.Suspense>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Home />,
      },
      {
        path: "about",
        element: <About />,
      },
      {
        path: "contact",
        element: <Contact />,
      },
      {
        path: "books",
        element: <Books />,
      },
      {
        path: "philosophy",
        element: <Philosophy />,
      },
      {
        path: "resources",
        element: <Resources />,
      },
      {
        path: "resources/curriculum",
        element: <Curriculum />,
      },
      {
        path: "resources/pedagogy",
        element: <Pedagogy />,
      },
      {
        path: "resources/assessment",
        element: <Assessment />,
      },
      // Public QR-scan resources hub — deliberately NOT under /resources
      // (the static marketing hub above); see docs/plans/book-resources.md.
      // Must be public and never require auth.
      {
        path: "book-resources",
        element: <BookResources />,
      },
      {
        path: "book-resources/:themeCode",
        element: <BookResourceDetail />,
      },
      {
        path: "blog",
        element: <BlogList />,
      },
      {
        path: "blog/:id",
        element: <BlogDetail />,
      },
      // ─── Ecommerce routes (SHOP-01) ───────────────────────────────────────
      // Guarded by REACT_APP_ENABLE_SHOP env var. When false, all /cart,
      // /checkout, and /quote routes simply don't exist (404).
      // NOTE: There is NO standalone /shop page. Product/book browsing
      // is handled by /books (Books.jsx). Bundle & product detail pages
      // live under /books/* for a unified catalog experience.
      //
      // IMPORTANT: Specific routes (product/:slug, bundle/:slug) MUST come
      // before the catch-all :slug to prevent "product" being matched as a slug.
      ...(featureFlags.ENABLE_SHOP ? [
        { path: 'books/product/:slug',  element: <ProductDetail /> },
        { path: 'books/bundle/:slug',   element: <BundleDetail /> },
        { path: 'cart',                 element: <CartPage /> },
        { path: 'checkout',             element: <CheckoutPage /> },
        { path: 'order-confirm/:id',    element: <OrderConfirm /> },
        { path: 'account/orders',       element: <OrderHistory /> },
        { path: 'account/orders/:id',   element: <OrderDetail /> },
        { path: 'account/downloads',    element: <Downloads /> },
        { path: 'account/returns',      element: <Returns /> },
        { path: 'account/quotes',       element: <QuoteHistory /> },
        { path: 'account/educator-verify', element: <EducatorVerification /> },
        { path: 'quote',                element: <QuotePage /> },
      ] : []),
      // Profile is always available, not gated behind shop flag
      { path: 'account/profile',         element: <ProfilePage /> },
      { path: 'books/:slug',            element: <ProductDetail source="books" /> },
      // 404 catch-all — must be last
      { path: '*',                       element: <NotFoundPage /> },
    ],
    },
    {
      path: "/login",
      element: (
        <React.Suspense fallback={ShopFallback}>
          <AuthLayout>
            <LoginForm />
          </AuthLayout>
        </React.Suspense>
      ),
    },
    {
      path: "/register",
      element: (
        <React.Suspense fallback={ShopFallback}>
          <AuthLayout>
            <RegisterForm />
          </AuthLayout>
        </React.Suspense>
      ),
    },
    // Admin Routes
    {
      path: "/admin",
      element: (
        <React.Suspense fallback={ShopFallback}>
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        </React.Suspense>
      ),
      children: [
        {
          index: true,
          element: <Dashboard />,
        },
        {
          path: "users",
          element: <UsersPage />,
        },
        {
          path: "blogs",
          element: <BlogsPage />,
        },
        {
          path: "books",
          element: <BooksPage />,
        },
        {
          path: "book-resources",
          element: <BookResourcesPage />,
        },
        {
          path: "categories",
          element: <CategoriesPage />,
        },
        {
          path: "orders",
          element: <OrdersPage />,
        },
        {
          path: "bundles",
          element: <BundlesPage />,
        },
        {
          path: "store-categories",
          element: <StoreCategoriesPage />,
        },
        {
          path: "quotes",
          element: <QuotesPage />,
        },
        {
          path: "coupons",
          element: <CouponsPage />,
        },
      ],
    },
]);

// GA4's automatic pageview only fires once, at initial gtag('config', ...)
// (see loadGoogleAnalytics.js — send_page_view is disabled there for
// exactly this reason). React Router navigates client-side with no full
// reload, so without this, every page after the very first would be
// invisible in GA4 — including this app's login/register/admin routes,
// which sit outside <Layout>. Subscribing directly on the router
// instance (rather than a component using useLocation) covers all of
// them uniformly, admin included, from one place.
let lastTrackedPath = null;
router.subscribe((state) => {
  const path = state.location.pathname + state.location.search;
  if (path === lastTrackedPath) return; // React Router can fire on non-navigation state changes too
  lastTrackedPath = path;
  // Deferred one tick: at the instant router state updates, the new
  // page hasn't rendered yet, so document.title (set by <SEO> in a
  // useEffect) would still reflect the page being navigated away from.
  setTimeout(() => {
    track('page_view', {
      page_path: path,
      page_location: window.location.origin + path,
      page_title: document.title,
    });
  }, 0);
});

export default router;
